import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getEnvironmentNetworkInfoQueryKey } from "components/Environment/networkInfo";
import { ExternalNode } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";
import {
  NetworkMemberStatus,
  NetworkMemberType,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { WorkspaceStatus } from "models/Workspace.model";
import { getEnvironmentStatus } from "components/Environment/utils/environtmentUtils";
import {
  useOrganizationComputeResourcesQuery,
  useOrganizationComputeResourcesWithConnectivityQuery,
} from "server/devplane/organizations/queries";
import type {
  OrganizationComputeResource,
  OrganizationComputeResourceFilters,
} from "server/devplane/organizations/api";
import { useEnvironmentNetworkMemberTypes } from "./useEnvironmentNetworkMemberTypes";

export type ActivityFilter = "all" | "active" | "inactive";
export type ComputeTypeFilter = "all" | "cloud" | "local";
export type NetworkFilter = "all" | "meshed" | "isolated" | "egress-only";

interface UseOrganizationComputeResourceListOptions {
  activity: ActivityFilter;
  computeType: ComputeTypeFilter;
  createdByUserId?: string;
  network: NetworkFilter;
  organizationId: string;
  search: string;
}

const isCloudComputeActive = (status: WorkspaceStatus) =>
  status !== WorkspaceStatus.Stopped && status !== WorkspaceStatus.Failure;

const matchesActivityFilter = (
  resource: OrganizationComputeResource,
  activity: ActivityFilter,
) => {
  if (activity === "all") return true;

  const active =
    resource.type === "cloud"
      ? isCloudComputeActive(getEnvironmentStatus(resource.environment))
      : resource.node.connectivityInfo?.status ===
        NetworkMemberStatus.CONNECTED;
  return activity === "active" ? active : !active;
};

const matchesSearch = (
  resource: OrganizationComputeResource,
  normalizedSearch: string,
) => {
  if (!normalizedSearch) return true;
  if (resource.type === "cloud") {
    return Boolean(
      resource.environment.name?.toLowerCase().includes(normalizedSearch) ||
      resource.environment.workspaceId
        ?.toLowerCase()
        .includes(normalizedSearch),
    );
  }
  return Boolean(
    resource.node.name?.toLowerCase().includes(normalizedSearch) ||
    resource.node.externalNodeId.toLowerCase().includes(normalizedSearch),
  );
};

const haveSameResources = (
  left: readonly OrganizationComputeResource[],
  right: readonly OrganizationComputeResource[],
) =>
  left.length === right.length &&
  left.every((resource, index) => resource === right[index]);

interface ComputeListPresentationOptions {
  activity: ActivityFilter;
  filteredResources: OrganizationComputeResource[];
  isBaseRequestPending: boolean;
  isFilterDataSettled: boolean;
  network: NetworkFilter;
  organizationId: string;
  requestKey: string;
}

function useComputeListPresentation({
  activity,
  filteredResources,
  isBaseRequestPending,
  isFilterDataSettled,
  network,
  organizationId,
  requestKey,
}: ComputeListPresentationOptions) {
  const lifecycle = useRef({
    hasCompletedInitialLoad: false,
    organizationId,
    pendingRequestKey: undefined as string | undefined,
    requestKey,
  });
  const [lastReadyResult, setLastReadyResult] = useState<{
    organizationId: string;
    resources: OrganizationComputeResource[];
  }>({ organizationId, resources: [] });

  if (lifecycle.current.organizationId !== organizationId) {
    lifecycle.current = {
      hasCompletedInitialLoad: false,
      organizationId,
      pendingRequestKey: undefined,
      requestKey,
    };
  } else if (lifecycle.current.requestKey !== requestKey) {
    lifecycle.current.requestKey = requestKey;
    lifecycle.current.pendingRequestKey = requestKey;
  }

  const hasClientFilters = activity !== "all" || network !== "all";
  const serverFilteredDataReady =
    !isBaseRequestPending && (!hasClientFilters || isFilterDataSettled);
  const isInitialLoading =
    !lifecycle.current.hasCompletedInitialLoad && !serverFilteredDataReady;
  const isServerFilterLoading =
    lifecycle.current.pendingRequestKey === requestKey &&
    !serverFilteredDataReady;
  const isLoading = isInitialLoading || isServerFilterLoading;
  const canShowCurrentResult =
    !isLoading && (!hasClientFilters || isFilterDataSettled);

  useEffect(() => {
    if (serverFilteredDataReady) {
      lifecycle.current.hasCompletedInitialLoad = true;
      if (lifecycle.current.pendingRequestKey === requestKey) {
        lifecycle.current.pendingRequestKey = undefined;
      }
    }

    if (canShowCurrentResult) {
      setLastReadyResult((currentResult) =>
        currentResult.organizationId === organizationId &&
        haveSameResources(currentResult.resources, filteredResources)
          ? currentResult
          : { organizationId, resources: filteredResources },
      );
    }
  }, [
    canShowCurrentResult,
    filteredResources,
    organizationId,
    requestKey,
    serverFilteredDataReady,
  ]);

  const previousResources =
    lastReadyResult.organizationId === organizationId
      ? lastReadyResult.resources
      : [];

  return {
    computeResources: canShowCurrentResult
      ? filteredResources
      : previousResources,
    isLoading,
  };
}

// Refresh active network-info queries after each successful list snapshot, even
// when list contents are unchanged. Initial mounts load their own network info;
// cached inactive queries and requests already in flight do not add API calls.
function useNetworkInfoSnapshotSync(
  organizationId: string,
  resources: OrganizationComputeResource[] | undefined,
  dataUpdatedAt: number,
) {
  const queryClient = useQueryClient();
  const lastNetworkRefresh = useRef<{
    organizationId: string;
    dataUpdatedAt: number;
  } | null>(null);
  useEffect(() => {
    if (!resources) return;
    const previous = lastNetworkRefresh.current;
    lastNetworkRefresh.current = {
      organizationId,
      dataUpdatedAt,
    };
    if (
      !previous ||
      previous.organizationId !== organizationId ||
      previous.dataUpdatedAt === dataUpdatedAt
    )
      return;
    for (const resource of resources) {
      if (resource.type !== "cloud" || !resource.environment.environment_id)
        continue;
      const queryKey = getEnvironmentNetworkInfoQueryKey(
        resource.environment.environment_id,
      );
      if (
        (queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0) >=
        dataUpdatedAt
      )
        continue;
      void queryClient
        .invalidateQueries(
          {
            queryKey,
            refetchType: "active",
          },
          { cancelRefetch: false },
        )
        .catch(() => undefined);
    }
  }, [organizationId, resources, dataUpdatedAt, queryClient]);
}

export function useOrganizationComputeResourceList({
  activity,
  computeType,
  createdByUserId,
  network,
  organizationId,
  search,
}: UseOrganizationComputeResourceListOptions) {
  const requestFilters = useMemo<OrganizationComputeResourceFilters>(
    () => ({
      type: computeType,
      ...(createdByUserId ? { createdByUserId } : {}),
    }),
    [computeType, createdByUserId],
  );
  // Both passes start together. The fast pass renders the rows; the full pass
  // supplies the local-node status and network details when it finishes.
  const fastQuery = useOrganizationComputeResourcesQuery(
    organizationId,
    requestFilters,
  );
  const connectivityQuery =
    useOrganizationComputeResourcesWithConnectivityQuery(
      organizationId,
      requestFilters,
    );
  useNetworkInfoSnapshotSync(
    organizationId,
    fastQuery.data,
    fastQuery.dataUpdatedAt,
  );
  const fastResources = fastQuery.data ?? [];
  const connectivityByNodeId = useMemo(
    () =>
      new Map(
        (connectivityQuery.data ?? []).flatMap((resource) =>
          resource.type === "local"
            ? ([[resource.node.externalNodeId, resource.node]] as const)
            : [],
        ),
      ),
    [connectivityQuery.data],
  );
  const resources = useMemo(
    () =>
      fastResources.map((resource): OrganizationComputeResource => {
        if (resource.type === "cloud") return resource;

        const connectivityNode = connectivityByNodeId.get(
          resource.node.externalNodeId,
        );
        return connectivityNode
          ? {
              type: "local",
              node: new ExternalNode({
                ...resource.node,
                connectivityInfo: connectivityNode.connectivityInfo,
                networkMemberType: connectivityNode.networkMemberType,
              }),
            }
          : resource;
      }),
    [connectivityByNodeId, fastResources],
  );
  const cloudEnvironments = useMemo(
    () =>
      resources.flatMap((resource) =>
        resource.type === "cloud" ? [resource.environment] : [],
      ),
    [resources],
  );
  const {
    memberTypes: environmentNetworkMemberTypes,
    isSettled: isEnvironmentNetworkInfoSettled,
  } = useEnvironmentNetworkMemberTypes(cloudEnvironments, network !== "all");
  const normalizedSearch = search.trim().toLowerCase();
  const requestedNetworkMemberType =
    network === "meshed"
      ? NetworkMemberType.SHARED
      : network === "egress-only"
        ? NetworkMemberType.EGRESS_ONLY
        : NetworkMemberType.ISOLATED;

  const filteredResources = useMemo(
    () =>
      resources.filter((resource) => {
        if (!matchesActivityFilter(resource, activity)) return false;
        if (!matchesSearch(resource, normalizedSearch)) return false;
        if (network === "all") return true;

        const networkMemberType =
          resource.type === "cloud"
            ? environmentNetworkMemberTypes.get(
                resource.environment.workspaceId ||
                  resource.environment.environment_id ||
                  "",
              )
            : resource.node.networkMemberType;
        return networkMemberType === requestedNetworkMemberType;
      }),
    [
      activity,
      environmentNetworkMemberTypes,
      network,
      normalizedSearch,
      requestedNetworkMemberType,
      resources,
    ],
  );
  const requestKey = `${organizationId}:${computeType}:${createdByUserId ?? ""}`;
  const isBaseRequestPending = fastQuery.isLoading || fastQuery.isFetching;
  const isConnectivityRequestPending =
    connectivityQuery.isLoading || connectivityQuery.isFetching;
  const hasClientFilters = activity !== "all" || network !== "all";
  const hasLocalResources = resources.some(
    (resource) => resource.type === "local",
  );
  const isFilterDataSettled =
    (!hasClientFilters || !isBaseRequestPending) &&
    (!hasLocalResources || !isConnectivityRequestPending) &&
    (network === "all" || isEnvironmentNetworkInfoSettled);
  const { computeResources, isLoading } = useComputeListPresentation({
    activity,
    filteredResources,
    isBaseRequestPending,
    isFilterDataSettled,
    network,
    organizationId,
    requestKey,
  });
  const refetchFastResources = fastQuery.refetch;
  const refetchConnectivityResources = connectivityQuery.refetch;
  const refetch = useCallback(
    () => Promise.all([refetchFastResources(), refetchConnectivityResources()]),
    [refetchConnectivityResources, refetchFastResources],
  );

  return {
    cloudEnvironments,
    computeResources,
    hasCurrentQueryResources: fastResources.length > 0,
    isError: !isLoading && fastQuery.isError && fastResources.length === 0,
    isLocalConnectivityLoading:
      hasLocalResources && connectivityQuery.isLoading,
    isLoading,
    isPageRequestPending: isBaseRequestPending,
    refetch,
  };
}
